import { BaseType } from '../types/Enums';
import type { BaseComponent, Building, PhotoLocation } from '../types/protocols';

/**
 * Pins in parent-surface photos: `photoLocations/{photoKey}` on windows, doors
 * and mounted utilities (workspace CLAUDE.md, schema). A component may be
 * marked in several photos; every rule that picks one or orders them lives
 * here so the cards, lists and grouping agree.
 *
 * - Side is derived from the parent surface's type, never stored: facade and
 *   roof photos are outer ("utsida"), wall and ceiling photos inner ("insida").
 *   A pin whose parent cannot be read counts as inner.
 * - Primary pin: an outer pin beats an inner one; within a side the oldest pin
 *   wins, i.e. the smallest photo key (push keys sort chronologically).
 */

export type PhotoLocationSide = 'outer' | 'inner';

export const PHOTO_LOCATION_SIDE_LABELS: Record<PhotoLocationSide, string> = {
    outer: 'utsida',
    inner: 'insida',
};

/** One pin together with its photo key and derived side. */
export interface PhotoLocationEntry {
    photoKey: string;
    location: PhotoLocation;
    side: PhotoLocationSide;
}

const OUTER_PARENT_TYPES: ReadonlySet<string> = new Set([BaseType.Facade, BaseType.Roof]);

/** Side of a pin from its parent surface's type; inner when the parent is unknown. */
export const photoLocationSide = (loc: PhotoLocation, building: Building | null | undefined): PhotoLocationSide => {
    const parentType = building?.model?.structure?.components?.[loc.parentKey]?.type;
    return parentType && OUTER_PARENT_TYPES.has(parentType) ? 'outer' : 'inner';
};

const compareEntries = (a: PhotoLocationEntry, b: PhotoLocationEntry): number => {
    if (a.side !== b.side) return a.side === 'outer' ? -1 : 1;
    return a.photoKey < b.photoKey ? -1 : a.photoKey > b.photoKey ? 1 : 0;
};

/**
 * Every pin of the component in display order: outer pins before inner, each
 * side by photo key (oldest first). The first entry is the primary pin.
 * Entries without a storage path are skipped.
 */
export const photoLocationList = (
    component: Pick<BaseComponent, 'photoLocations'>,
    building: Building | null | undefined
): PhotoLocationEntry[] => {
    const pins = component.photoLocations;
    if (!pins) return [];
    return Object.entries(pins)
        .filter(([, location]) => !!location?.storagePath)
        .map(([photoKey, location]) => ({ photoKey, location, side: photoLocationSide(location, building) }))
        .sort(compareEntries);
};

/** The one pin a single-thumbnail view shows, or null when the component has no pin. */
export const primaryPhotoLocation = (
    component: Pick<BaseComponent, 'photoLocations'>,
    building: Building | null | undefined
): PhotoLocation | null => photoLocationList(component, building)[0]?.location ?? null;

/** Whether the component is marked in at least one photo. */
export const hasPhotoLocation = (component: Pick<BaseComponent, 'photoLocations'>): boolean =>
    !!component.photoLocations && Object.values(component.photoLocations).some(loc => !!loc?.storagePath);

/** Label for a pin's marker, e.g. "Position i foto (utsida)". */
export const photoLocationLabel = (side: PhotoLocationSide): string =>
    `Position i foto (${PHOTO_LOCATION_SIDE_LABELS[side]})`;

// ---------------------------------------------------------------------------
// Counts
//
// A component pinned in several photos appears under each of them, which reads
// as more components than exist, so the views state how many distinct
// components they show. Selecting a marker highlights that component in every
// photo it is pinned in.
// ---------------------------------------------------------------------------

/** Singular and plural type names for count texts, lower case, e.g. "fönster"/"fönster", "dörr"/"dörrar". */
export const componentTypeCountNames = (type: BaseType | undefined, translated: string): { singular: string; plural: string } => {
    const singular = translated.toLocaleLowerCase('sv');
    const plural = type === BaseType.Door ? 'dörrar' : type === BaseType.Utility ? 'installationer' : singular;
    return { singular, plural };
};

const countText = (count: number, singular: string, plural: string): string =>
    `${count} ${count === 1 ? singular : plural}`;

/** Line above a photo grid: distinct components and photos, e.g. "12 fönster i 5 foton". */
export const photoLocationSummaryText = (componentCount: number, photoCount: number, singular: string, plural: string): string =>
    `${countText(componentCount, singular, plural)} i ${countText(photoCount, 'foto', 'foton')}`;
